import type {ReactNode} from 'react'
import {Form, Link, useNavigate, useNavigation, useRouteLoaderData, useSearchParams} from 'react-router'
import {useState, useEffect, useRef} from 'react'
import {track} from '@core/utils/analytics'
import {magicLinkErrorCopy, authErrorCopy} from '@core/auth-ui/authErrorCopy'
import {GoogleGIcon} from '@core/auth-ui/GoogleGIcon'
import {FacebookSignIn} from '@core/auth-ui/FacebookSignIn'
import {SignInCodeForm} from '@core/auth-ui/SignInCodeForm/SignInCodeForm'
import type {loader as rootLoader} from '@root'
import defaultStyles from './SignInCard.module.scss'

/**
 * The sign-in card — one component, every house that opens one (plan
 * `thinking/plans/2026_09_17_ovid_one_wall.md` § Decided 6). It was Toile's own
 * (`products/toile/components/SignInCard/`, itself the narcis port of plan
 * 2026_08_30_toile_sign_in decision 2) and moved here whole: Google first then magic link, inline
 * email validation, in-flight spinner, sent state, dismissable error banner, `redirectTo` /
 * `gate-trigger` bridges — every behaviour kept, none re-derived, including the Enter-key trap
 * `Column.tsx:285-288` documents around the Google submit button.
 *
 * What a house supplies: its `glyph`, the two lines above the forms, the terms/privacy hrefs, and —
 * if its surface asks for one — its own `styles`. The default stylesheet beside this file is the one
 * Toile shipped, written against the `--tl-*` wall tokens the shared wall UI already reads
 * (`Tile.module.scss`'s own note on that namespace), so a second house needs no stylesheet of its
 * own to mount a card that belongs to the wall it sits over.
 *
 * `narcisTrack` calls do not travel: `gate_hit`, `auth_option_selected` and `magic_link_sent` are all
 * core events (CoreEventMap, @core/utils/analytics), so they fire through the shared `track`.
 */

/** The two lines a house owns above the forms. Everything else the card says — the sent state, the
 *  retry, the consent line, the back link — is the same act in every house, so it is stated once,
 *  below, rather than asked of each caller. */
export type SignInCardCopy = {
    title: string
    subtitle: string
}

const DEFAULT_COPY: SignInCardCopy = {title: 'Sign in', subtitle: 'Your results stay yours, on every device.'}

// Spinner — used for Google in-flight state. Takes the class from the caller's own stylesheet,
// since the card's sheet is a prop now.
function Spinner({className}: {className?: string}) {
    return (
        <svg className={className} width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" strokeDasharray="28" strokeDashoffset="10" strokeLinecap="round"/>
        </svg>
    )
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

type SignInCardProps = {
    googleAuthAction: string
    /** The house's lockup above the title: its mark, and optionally the word under it (Toile's
     *  stacked lockup — mark 42 over the word 15, `SignIn.dc.html`). Nothing rendered without one. */
    glyph?: {mark: ReactNode; word?: string}
    /** The house's own two lines; the shared defaults otherwise. */
    copy?: SignInCardCopy
    /** Where the consent line's two links point. Each house shelves its policy at its own URL. */
    termsHref?: string
    privacyHref?: string
    /** The house's own stylesheet, when its surface asks for one — the class-name contract is this
     *  file's own default module. */
    styles?: Record<string, string>
    /** The magic-link field's DOM id and its label's `htmlFor`. Defaulted, and overridden only where
     *  two cards can be in one document at once (Ovid's wall carries one inline at its end and one
     *  in the sign-in layer) — two elements sharing an id is what breaks a label's association. */
    inputId?: string
    // Forwarded search param error keys from URL (e.g. auth-error from Google callback)
    authError?: string | null
    // An overlay caller (the wall's sign-in gate) hands these down directly rather than writing
    // them to the URL first: the wall keeps its URL a mirror of the search, and a param round-trip
    // through the router would re-run its loader. The URL remains the fallback for the redirect
    // gates that arrive here as a navigation.
    redirectTo?: string | null
    gateTrigger?: string | null
    /** The back link's act. Absent: the page's own back (referrer, else `/`), for the card as a page.
     *  A function: an overlay's close. `null`: no back link, for a card set in the page's own flow. */
    onBack?: (() => void) | null
}

export function SignInCard({
    googleAuthAction, authError, redirectTo: redirectToProp, gateTrigger: gateTriggerProp,
    glyph, copy = DEFAULT_COPY, termsHref = '/terms', privacyHref = '/privacy',
    styles = defaultStyles, inputId = 'magic-link-email', onBack,
}: SignInCardProps) {
    // Single source of truth: the shared root loader mints the nonce once per session; this card
    // hand-rolls its own magic-link form (it doesn't go through @core's MagicLinkForm), so it reads
    // the value the same way MagicLinkForm does rather than taking it as a prop.
    const rootData = useRouteLoaderData<typeof rootLoader>('root')
    const nonce = rootData?.magicLinkNonce
    const [searchParams, setSearchParams] = useSearchParams()
    const navigation = useNavigation()
    const navigate = useNavigate()

    const [email, setEmail] = useState('')
    const [emailTouched, setEmailTouched] = useState(false)
    const [emailError, setEmailError] = useState<string | null>(null)
    const [googleInflight, setGoogleInflight] = useState(false)
    const [dismissedBanner, setDismissedBanner] = useState(false)

    // `setAuthReturn` (core/auth/auth.server.ts) only reads a submitted `redirectTo` FORM field —
    // it has to survive the OIDC round-trip, so a bare ?redirectTo= in the URL is dropped. A route
    // that gates on sign-in sends the user here with that param, so both forms must carry it through
    // as a hidden field or the user lands on / instead of where they were headed. Nothing on toile
    // gates yet (plan decision 6); the bridge is here so mounting a gate later is a one-line change.
    const redirectTo = redirectToProp ?? searchParams.get('redirectTo')

    // gate-trigger: which surface's gate redirected here. Read as a URL param, not a form field,
    // and posted back out as `trigger` on both forms — `setAuthReturn` / `magic-link.server.ts`
    // read `formData.get('trigger')`, `popAuthTrigger` returns it on the far side, and root.tsx maps
    // it onto sign_up's closed enum. Absent on a plain /auth/login visit or a link-expired bounce,
    // where the default below is the honest answer.
    const gateTriggerParam = gateTriggerProp ?? searchParams.get('gate-trigger')
    const trigger = gateTriggerParam ?? 'login-page'

    // URL-param driven state (magic-link server responses redirect back here)
    const magicLinkSent = searchParams.get('magic-link-sent') === '1'
    const magicLinkErrorParam = searchParams.get('magic-link-error')
    const magicLinkError = magicLinkErrorCopy(magicLinkErrorParam)

    // Scope `submitting` to the magic-link form only. `useNavigation()` returns router-global
    // state, so `navigation.state === 'submitting'` would be true while the Google form is
    // submitting too — which would falsely lock the magic-link input read-only. Path-scope to
    // the magic-link action route. The Google button has its own `googleInflight` flag.
    const submitting =
        navigation.state === 'submitting' && navigation.formAction === '/auth/magic-link'

    // Clear search params from URL after reading them once
    useEffect(() => {
        if (magicLinkSent || magicLinkErrorParam || authError || gateTriggerParam) {
            const url = new URL(window.location.href)
            url.searchParams.delete('magic-link-sent')
            url.searchParams.delete('magic-link-error')
            url.searchParams.delete('auth-error')
            url.searchParams.delete('gate-trigger')
            window.history.replaceState({}, '', url.pathname + url.search)
        }
    }, [magicLinkSent, magicLinkErrorParam, authError, gateTriggerParam])

    // GA4: gate_hit for the redirect gate that sent the user here — fires once, mirrors every other
    // gate call site. Absent gate-trigger (a plain /auth/login visit) fires nothing; this page has
    // no gate of its own to report.
    const gateHitFiredRef = useRef(false)
    useEffect(() => {
        if (!gateTriggerParam || gateHitFiredRef.current) return
        gateHitFiredRef.current = true
        track('gate_hit', {gate: 'signup', trigger: gateTriggerParam})
    }, [gateTriggerParam])

    // GA4: magic_link_sent — fires once when this component mounts already in the "sent" state
    // (see magic_link_sent's comment, @core/utils/analytics.ts, for why this mount is the
    // truthful fire point).
    const magicLinkSentFiredRef = useRef(false)
    useEffect(() => {
        if (!magicLinkSent || magicLinkSentFiredRef.current) return
        magicLinkSentFiredRef.current = true
        track('magic_link_sent', {trigger})
    }, [magicLinkSent, trigger])

    // Reset banner dismiss when a new error arrives
    useEffect(() => {
        setDismissedBanner(false)
    }, [magicLinkError, authError])

    // Back through Google restores this page from the back-forward cache as it was left: mid-submit,
    // "Connecting to Google…", while the visitor may by now be signed in. A reload lets the loader
    // decide — forward a signed-in visitor to redirectTo, show a fresh card otherwise (dev, 2026-10-22).
    useEffect(() => {
        const onPageShow = (event: PageTransitionEvent) => {
            if (event.persisted) window.location.reload()
        }
        window.addEventListener('pageshow', onPageShow)
        return () => window.removeEventListener('pageshow', onPageShow)
    }, [])

    function validateEmail(value: string): string | null {
        if (!value.trim()) return 'Enter a valid email.'
        if (!EMAIL_REGEX.test(value.trim())) return 'Enter a valid email.'
        return null
    }

    function handleEmailBlur() {
        setEmailTouched(true)
        setEmailError(validateEmail(email))
    }

    function handleSubmit(e: React.FormEvent) {
        const err = validateEmail(email)
        if (err) {
            e.preventDefault()
            setEmailTouched(true)
            setEmailError(err)
            return
        }
        track('auth_option_selected', {method: 'email', trigger})
    }

    const showBanner = !dismissedBanner && !!(authError || magicLinkError)
    const bannerText = authError ? authErrorCopy(authError) : magicLinkError

    // Back link — go to referrer if same-origin and not /auth/*, otherwise /
    function handleBack(e: React.MouseEvent<HTMLAnchorElement>) {
        e.preventDefault()
        if (onBack) {
            onBack()
            return
        }
        const ref = document.referrer
        if (ref) {
            try {
                const refUrl = new URL(ref)
                if (refUrl.origin === window.location.origin && !refUrl.pathname.startsWith('/auth/')) {
                    navigate(refUrl.pathname + refUrl.search + refUrl.hash)
                    return
                }
            } catch {}
        }
        navigate('/')
    }

    return (
        <div className={styles.card}>
            {glyph && (
                <div className={styles.lockup} aria-hidden="true">
                    {glyph.mark}
                    {glyph.word && <span className={styles.word}>{glyph.word}</span>}
                </div>
            )}

            <h1 className={styles.title}>{magicLinkSent ? 'Check your inbox' : copy.title}</h1>
            <p className={styles.subtitle}>
                {magicLinkSent
                    ? `A sign-in link is on its way to ${email || 'your email'}.`
                    : copy.subtitle}
            </p>

            {/* Error banner — above the Google button, dismissable. Surface-grey with the hue on the
                text and nowhere else (Errors.dc.html: never a red fill). */}
            {showBanner && bannerText && (
                <div className={styles.errorBanner} role="alert">
                    <span className={styles.errorDot} aria-hidden="true" />
                    <span className={styles.errorText}>{bannerText}</span>
                    <button
                        type="button"
                        className={styles.errorBannerDismiss}
                        onClick={() => setDismissedBanner(true)}
                        aria-label="Dismiss"
                    >
                        ×
                    </button>
                </div>
            )}

            {magicLinkSent ? (
                <>
                <div className={styles.sentBlock} role="status">
                    <span className={styles.sentCheck} aria-hidden="true">✓</span>
                    <div>
                        <b className={styles.sentHeading}>Link sent</b>
                        <span className={styles.sentBody}>The link works once and expires in 15 minutes.</span>
                    </div>
                </div>
                <SignInCodeForm />
                </>
            ) : (
                <>
                    {/* Google sign-in */}
                    {/* reloadDocument: the OAuth bounce must be a full-document POST, not a SPA .data
                        fetch — Safari content blockers kill fetches whose URL matches google-tracker
                        patterns (observed on narcis 2026-07-12: browser-side 400 on
                        /auth/google.data, request never reached the server), and we navigate away to
                        Google regardless. */}
                    <Form
                        action={googleAuthAction}
                        method="POST"
                        className={styles.googleForm}
                        reloadDocument
                        onSubmit={() => {
                            setGoogleInflight(true)
                            track('auth_option_selected', {method: 'google', trigger})
                        }}
                    >
                        <input type="hidden" name="trigger" value={trigger} />
                        {redirectTo && <input type="hidden" name="redirectTo" value={redirectTo} />}
                        <button type="submit" className={styles.googleBtn}>
                            {googleInflight ? <Spinner className={styles.spinner} /> : <GoogleGIcon />}
                            <span>{googleInflight ? 'Connecting to Google…' : 'Continue with Google'}</span>
                        </button>
                    </Form>
                    <FacebookSignIn formClassName={styles.googleForm} className={styles.googleBtn} redirectTo={redirectTo} trigger={trigger} />

                    <div className={styles.divider}>
                        <span>or</span>
                    </div>

                    <Form method="POST" action="/auth/magic-link" onSubmit={handleSubmit}>
                        <input type="hidden" name="nonce" value={nonce ?? ''} />
                        <input type="hidden" name="trigger" value={trigger} />
                        {redirectTo && <input type="hidden" name="redirectTo" value={redirectTo} />}
                        <div className={styles.magicRow}>
                            <label htmlFor={inputId} className="sr-only">Email address</label>
                            <input
                                type="email"
                                name="email"
                                id={inputId}
                                placeholder="you@example.com"
                                value={email}
                                onChange={e => {
                                    setEmail(e.target.value)
                                    if (emailTouched) setEmailError(validateEmail(e.target.value))
                                }}
                                onBlur={handleEmailBlur}
                                className={emailTouched && emailError ? `${styles.emailInput} ${styles.emailInputError}` : styles.emailInput}
                                readOnly={submitting}
                                autoComplete="email"
                            />
                            <button type="submit" className={styles.magicBtn} disabled={submitting}>
                                {submitting ? 'Sending…' : 'Send link'}
                            </button>
                        </div>
                        {emailTouched && emailError && (
                            <p className={styles.fieldError}>{emailError}</p>
                        )}
                    </Form>
                </>
            )}

            {magicLinkSent && (
                <button
                    type="button"
                    className={styles.retryLink}
                    onClick={() => {
                        setEmail('')
                        setEmailError(null)
                        setEmailTouched(false)
                        setSearchParams({}, {replace: true})
                    }}
                >
                    Use a different email
                </button>
            )}

            {/* Acceptance by continuing, no checkbox, not tracked — the same consent line narcis's
                card carries. SignIn.dc.html omits it; the canvas is schematic about the card's
                secondary lines (it omits the error banner too, which the plan requires kept). The
                two paths come from the caller now that the card is shared. */}
            <p className={styles.tos}>
                By continuing you agree to the <Link to={termsHref}>Terms</Link> and{' '}
                <Link to={privacyHref}>Privacy Policy</Link>.
            </p>

            {onBack !== null && (
                <a href="/" className={styles.backLink} onClick={handleBack}>
                    ← Back
                </a>
            )}
        </div>
    )
}
